// Customer "Мои заказы / Мои посылки" — combined view with two tabs.
//
// Orders tab: list of own orders with status timeline.
// Shipments tab: list of bundled shipments + a "consolidate received
// orders" composer at the top of Orders tab.
//
// Consolidation flow:
//   1. Orders tab shows received orders with checkboxes (others not
//      selectable)
//   2. Customer ticks ≥1, taps "Объединить в посылку" → POST /api/shipments
//   3. Shipments tab opens to the new building shipment
//   4. Customer can still add/remove (returns to received in Orders), or
//      tap "Готово, отправляйте" to mark ready
//   5. Operator fills tracking, marks shipped — every order in the
//      shipment moves to shipped status atomically
import { useState, useEffect, useCallback, useRef } from "react";
import { api } from "../api.js";
import { T, T_NEW } from "../theme.js";
import { Btn, Modal, Tabs, Mono, Serif, StatusPill } from "./ui.jsx";
import { useToast } from "./toast.jsx";
import { useT, useLang } from "../i18n.jsx";
import { InvoiceModal } from "./InvoiceModal.jsx";

const STATUS_FLOW = [
  "placed", "confirmed", "paid", "purchased", "received", "shipped", "delivered",
];

// Group orders by order_group_id (cart checkouts) while preserving the
// incoming newest-first order. A group with a single member — or no
// group id at all — renders as a plain row, exactly as before. Only
// real multi-item carts get the collapsible group wrapper.
function groupOrders(orders) {
  const buckets = new Map();
  const order = [];
  for (const o of orders) {
    const key = o.orderGroupId ? `g:${o.orderGroupId}` : `s:${o.id}`;
    if (!buckets.has(key)) { buckets.set(key, []); order.push(key); }
    buckets.get(key).push(o);
  }
  return order.map((key) => {
    const members = buckets.get(key);
    return members.length > 1
      ? { type: "group", key, members }
      : { type: "solo", key, order: members[0] };
  });
}

// The order's overall stage = the LEAST-advanced item's stage (the cart
// isn't "delivered" until every item is). Cancelled/unavailable items
// are ignored unless they're all terminal.
function groupStage(members) {
  const active = members.filter(
    (m) => m.status !== "cancelled" && m.status !== "unavailable");
  const pool = active.length > 0 ? active : members;
  let min = pool[0].status;
  let minIdx = STATUS_FLOW.indexOf(min);
  for (const m of pool) {
    const i = STATUS_FLOW.indexOf(m.status);
    if (i >= 0 && (minIdx < 0 || i < minIdx)) { min = m.status; minIdx = i; }
  }
  return min;
}

export function OrdersModal({ onClose, user, onOpenDm }) {
  const t = useT();
  const toast = useToast();
  const [tab, setTab] = useState("orders");
  const [orders, setOrders] = useState(null);
  const [shipments, setShipments] = useState(null);
  const [selected, setSelected] = useState(new Set());      // received-order ids picked for consolidation
  // Invoice composer: { orders:[...], orderNumber, customerName } | null
  const [invoiceData, setInvoiceData] = useState(null);
  const openInvoice = (members) => {
    const first = members[0] || {};
    setInvoiceData({
      orders: members,
      orderNumber: members.find((m) => m.orderNumber)?.orderNumber || null,
      customerName: first.resellerCustomer?.name || first.recipientName || null,
    });
  };

  const reload = useCallback(async () => {
    try {
      const [o, s] = await Promise.all([api.getMyOrders(), api.getMyShipments()]);
      setOrders(o);
      setShipments(s);
    } catch (e) { toast.error(e.message); setOrders([]); setShipments([]); }
  }, [toast]);
  useEffect(() => { reload(); }, [reload]);

  // Received orders that aren't already in any shipment — these are
  // selectable. Once an order joins a shipment, its shipment_id is set
  // and it falls out of this list.
  const consolidatable = (orders ?? []).filter(
    (o) => o.status === "received" /* shipment_id field not returned today; future API
            will add it. Until then we filter visually by absence of a shipped status. */,
  );

  const toggle = (id) => {
    setSelected((cur) => {
      const next = new Set(cur);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const consolidate = async () => {
    if (selected.size === 0) return;
    try {
      await api.createShipment([...selected]);
      toast.success(t("shipment.created.ok"));
      setSelected(new Set());
      setTab("shipments");
      await reload();
    } catch (e) { toast.error(e.message); }
  };

  return (
    <Modal title={t("orders.mine.title")} onClose={onClose} width={640}>
      <Tabs
        tabs={[
          { id: "orders", label: `${t("orders.tab.orders")}${orders ? ` (${orders.length})` : ""}` },
          { id: "shipments", label: `${t("orders.tab.shipments")}${shipments ? ` (${shipments.length})` : ""}` },
        ]}
        active={tab}
        onSelect={setTab}
      />

      {tab === "orders" && (
        <>
          {/* Consolidation bar — shows when 1+ received orders are
              selected. Sticks above the list. */}
          {consolidatable.length > 0 && (
            <div style={{ background: T_NEW.paperSoft, padding: 12, borderRadius: T_NEW.r2, marginBottom: T_NEW.s4,
                          display: "flex", alignItems: "center", gap: 12, fontSize: T_NEW.meta, color: T_NEW.ink56,
                          fontFamily: T_NEW.fontSans }}>
              <span style={{ flex: 1 }}>
                {selected.size > 0
                  ? t("orders.consolidate.selected", { n: selected.size })
                  : t("orders.consolidate.hint")}
              </span>
              <Btn disabled={selected.size === 0} onClick={consolidate}>
                {t("orders.consolidate.cta")}
              </Btn>
            </div>
          )}

          {orders === null ? (
            <div style={{ padding: 28, textAlign: "center", color: T_NEW.ink56, fontFamily: T_NEW.fontSans, fontSize: T_NEW.meta }}>{t("common.loading")}</div>
          ) : orders.length === 0 ? (
            <div style={{ padding: 28, textAlign: "center", color: T_NEW.ink56, fontFamily: T_NEW.fontSans, fontSize: T_NEW.bodySm, lineHeight: 1.55 }}>
              {t("orders.mine.empty")}
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {groupOrders(orders).map((entry) =>
                entry.type === "group" ? (
                  <OrderGroup key={entry.key} members={entry.members} t={t}
                    selected={selected} onToggle={toggle} onChanged={reload}
                    onInvoice={() => openInvoice(entry.members)} onOpenDm={onOpenDm} />
                ) : (
                  <OrderRow key={entry.order.id} o={entry.order} t={t}
                    selectable={entry.order.status === "received"}
                    selected={selected.has(entry.order.id)}
                    onToggle={() => toggle(entry.order.id)}
                    onChanged={reload}
                    onInvoice={() => openInvoice([entry.order])} onOpenDm={onOpenDm} />
                ),
              )}
            </div>
          )}
        </>
      )}

      {tab === "shipments" && (
        shipments === null ? (
          <div style={{ padding: 28, textAlign: "center", color: T_NEW.ink56, fontFamily: T_NEW.fontSans, fontSize: T_NEW.meta }}>{t("common.loading")}</div>
        ) : shipments.length === 0 ? (
          <div style={{ padding: 28, textAlign: "center", color: T_NEW.ink56, fontFamily: T_NEW.fontSans, fontSize: T_NEW.bodySm, lineHeight: 1.55 }}>
            {t("shipments.mine.empty")}
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {shipments.map((s) => <ShipmentRow key={s.id} s={s} t={t} onChanged={reload} />)}
          </div>
        )
      )}

      {invoiceData && (
        <InvoiceModal
          orders={invoiceData.orders}
          orderNumber={invoiceData.orderNumber}
          customerName={invoiceData.customerName}
          fromName={user?.displayName || null}
          onClose={() => setInvoiceData(null)}
        />
      )}
    </Modal>
  );
}

function OrderRow({ o, t, selectable, selected, onToggle, onChanged, hideOrderNumber, onInvoice, onOpenDm }) {
  // Both cancelled and unavailable are terminal "didn't go through"
  // states — render them with a similar red badge, just different copy
  // so the customer knows whether it was their cancel or a stockout.
  const cancelled = o.status === "cancelled";
  const unavailable = o.status === "unavailable";
  return (
    <div style={{ background: T_NEW.paperSoft, borderRadius: T_NEW.r2, padding: 16,
                  border: selected ? `1.5px solid ${T_NEW.persimmon}` : `1px solid ${T_NEW.ink12}`,
                  cursor: selectable ? "pointer" : "default" }}
         onClick={selectable ? onToggle : undefined}>
      <div style={{ display: "flex", gap: 12, marginBottom: 12 }}>
        {selectable && (
          <input type="checkbox" checked={selected} readOnly
                 style={{ width: 18, height: 18, marginTop: 20, accentColor: T_NEW.persimmon }} />
        )}
        {o.post?.imageUrl && (
          <img src={o.post.imageUrl} alt="" style={{ width: 64, height: 64, objectFit: "cover", borderRadius: T_NEW.r1, flexShrink: 0 }} />
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
          {o.orderNumber && !hideOrderNumber && (
            <Mono size={T_NEW.micro} color={T_NEW.persimmon}
                  style={{ display: "block", fontWeight: 700, marginBottom: 2 }}>
              № {o.orderNumber}
            </Mono>
          )}
          <Serif size={T_NEW.bodyLg} color={T_NEW.ink}
                 style={{ display: "block", lineHeight: 1.25, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {o.post?.title ?? t("orders.deletedPost")}
          </Serif>
          <Mono size={T_NEW.meta} color={T_NEW.ink56} style={{ display: "block", marginTop: 4 }}>
            {o.variant ? `${o.variant} · ` : ""}{t("order.qty")}: {o.qty}
            {o.qtyFulfilled != null && o.qtyFulfilled !== o.qty && (
              <span style={{ color: T_NEW.danger, fontWeight: 700, marginLeft: 4 }}>
                ({t("order.fulfilled.of", { fulfilled: o.qtyFulfilled, total: o.qty })})
              </span>
            )}
          </Mono>
          {o.notes && (
            <div style={{ fontSize: T_NEW.meta, color: T_NEW.ink56, marginTop: 4,
                          fontStyle: "italic", fontFamily: T_NEW.fontSerif }}>
              «{o.notes}»
            </div>
          )}
          {o.recipientName && (
            <div style={{ fontSize: T_NEW.micro, color: T_NEW.ink56, marginTop: 6,
                          fontFamily: T_NEW.fontSans }}>
              → {o.recipientName}
              {o.shipAddress ? ` · ${o.shipAddress.split("\n").join(", ")}` : ""}
            </div>
          )}
        </div>
        <div style={{ textAlign: "right", flexShrink: 0 }}>
          {/* Show the fulfilled total prominently when partial; the
              original "as ordered" total goes underneath in a dim
              strikethrough so the customer knows what they pay vs what
              they originally requested. Organizer fee (Feature 7) is
              broken out as a separate small line. */}
          {o.qtyFulfilled != null && o.qtyFulfilled !== o.qty && o.fulfilledTotalUsdCents != null ? (
            <>
              <Serif size={T_NEW.bodyLg} color={T_NEW.ink} style={{ display: "block", lineHeight: 1 }}>
                ${(o.fulfilledTotalUsdCents / 100).toFixed(2)}
              </Serif>
              <span style={{ fontSize: T_NEW.micro, color: T_NEW.ink56, fontFamily: T_NEW.fontMono, marginTop: 4, display: "block" }}>
                ≈ ₽{(o.fulfilledTotalRubCents / 100).toFixed(0)}
              </span>
              <span style={{ fontSize: T_NEW.micro, color: T_NEW.ink56, fontFamily: T_NEW.fontMono, marginTop: 2, display: "block", textDecoration: "line-through" }}>
                ${(o.totalUsdCents / 100).toFixed(2)}
              </span>
            </>
          ) : (
            <>
              <Serif size={T_NEW.bodyLg} color={T_NEW.ink} style={{ display: "block", lineHeight: 1 }}>
                ${((o.grandTotalUsdCents ?? o.totalUsdCents) / 100).toFixed(2)}
              </Serif>
              <span style={{ fontSize: T_NEW.micro, color: T_NEW.ink56, fontFamily: T_NEW.fontMono, marginTop: 4, display: "block" }}>
                ≈ ₽{((o.grandTotalRubCents ?? o.totalRubCents) / 100).toFixed(0)}
              </span>
              {o.organizerFeeUsdCents > 0 && (
                <span style={{ fontSize: T_NEW.micro, color: T_NEW.ink56, fontFamily: T_NEW.fontSans, marginTop: 4, display: "block" }}>
                  {t("order.organizerFee", { pct: o.organizerFeePctSnapshot })}: ${(o.organizerFeeUsdCents / 100).toFixed(2)}
                </span>
              )}
              {/* Cashback badge: pending before delivery (ochre tone),
                  earned (bottle green) after the ledger row landed.
                  Only show when the channel actually has a non-zero rate. */}
              {o.cashbackUsdCents > 0 && (
                <span style={{ fontSize: T_NEW.micro, marginTop: 6, display: "block",
                              color: o.cashbackCreditedAt ? T_NEW.bottle : T_NEW.ochre,
                              fontWeight: 700, fontFamily: T_NEW.fontSans, letterSpacing: 0.3 }}>
                  {o.cashbackCreditedAt
                    ? t("order.cashback.earned", { amt: (o.cashbackUsdCents / 100).toFixed(2) })
                    : t("order.cashback.pending", { amt: (o.cashbackUsdCents / 100).toFixed(2), pct: o.cashbackPctSnapshot })}
                </span>
              )}
            </>
          )}
        </div>
      </div>

      {cancelled || unavailable ? (
        <div style={{ padding: "10px 12px", background: `${T_NEW.danger}14`, borderRadius: T_NEW.r1,
                      fontSize: T_NEW.meta, color: T_NEW.danger, fontFamily: T_NEW.fontSans,
                      border: `1px solid ${T_NEW.danger}33` }}>
          <span style={{ fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.6 }}>
            {unavailable ? t("order.status.unavailable") : t("order.status.cancelled")} · {t(`order.status.${o.status}`)}
          </span>
          {o.cancelReason && (
            <div style={{ marginTop: 4, color: T_NEW.ink56, fontStyle: "italic", fontFamily: T_NEW.fontSerif }}>
              «{o.cancelReason}»
            </div>
          )}
        </div>
      ) : (
        <div style={{ display: "flex", gap: 4, fontSize: T_NEW.micro, color: T_NEW.ink56,
                      alignItems: "center", flexWrap: "wrap", fontFamily: T_NEW.fontMono }}>
          {STATUS_FLOW.map((s, i) => {
            const done = !!o[`${s}At`];
            const isCurrent = s === o.status;
            return (
              <div key={s} style={{ display: "flex", alignItems: "center", gap: 4 }}>
                <span style={{ display: "inline-block", width: 8, height: 8, borderRadius: "50%",
                               background: done ? T_NEW.persimmon : T_NEW.ink12,
                               boxShadow: isCurrent ? `0 0 0 3px ${T_NEW.persimmon}30` : "none" }} />
                <span style={{ color: done ? T_NEW.ink : T_NEW.ink56,
                               fontWeight: isCurrent ? 700 : 500,
                               textTransform: "uppercase", letterSpacing: 0.6 }}>
                  {t(`order.status.${s}`)}
                </span>
                {i < STATUS_FLOW.length - 1 && (
                  <span style={{ width: 12, height: 1, background: done ? T_NEW.persimmon : T_NEW.ink12, marginLeft: 2 }} />
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Reseller bookkeeping price (₽) for self-purchased orders. */}
      {!o.facilitated && (
        <ResellerPriceEditor order={o} onChanged={onChanged} t={t} />
      )}

      {/* Payment block — only for confirmed orders that have not yet
          had a screenshot uploaded. Once uploaded, we render the
          "awaiting review" state instead. Past 'confirmed' (paid /
          purchased / shipped / delivered) we hide the block entirely. */}
      {/* Receipt photos (Feature 4). Renders when there are any —
          relevant for orders at received/shipped/delivered. */}
      {o.receivedPhotoUrls?.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <Mono size={T_NEW.micro} color={T_NEW.ink56} style={{ display: "block", marginBottom: 6 }}>
            {t("order.received.photos") || "Фото при приёмке"}
          </Mono>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {o.receivedPhotoUrls.map((u, i) => (
              <a key={i} href={u} target="_blank" rel="noopener noreferrer">
                <img src={u} alt="" style={{ width: 64, height: 64, objectFit: "cover",
                                              borderRadius: T_NEW.r1, border: `1px solid ${T_NEW.ink12}` }} />
              </a>
            ))}
          </div>
        </div>
      )}

      {o.status === "confirmed" && !o.paymentUploadedAt && (
        <PaymentBlock order={o} onUploaded={onChanged} onOpenDm={onOpenDm} />
      )}
      {o.status === "confirmed" && o.paymentUploadedAt && (
        <div style={{ marginTop: 12, padding: "12px 14px", background: `${T_NEW.bottle}12`,
                      borderRadius: T_NEW.r1, fontSize: T_NEW.meta, color: T_NEW.ink,
                      display: "flex", gap: 10, alignItems: "center",
                      border: `1px solid ${T_NEW.bottle}33`, fontFamily: T_NEW.fontSans }}>
          <span style={{ color: T_NEW.bottle, fontWeight: 700, fontSize: T_NEW.bodySm }}>✓</span>
          <span style={{ flex: 1 }}>{t("order.payment.awaitingReview")}</span>
          {o.paymentScreenshotUrl && (
            <a href={o.paymentScreenshotUrl} target="_blank" rel="noopener noreferrer"
               style={{ color: T_NEW.persimmon, fontSize: T_NEW.micro, fontFamily: T_NEW.fontMono,
                        textTransform: "uppercase", letterSpacing: 0.6, textDecoration: "underline" }}>
              {t("order.payment.viewScreenshot")}
            </a>
          )}
        </div>
      )}

      {/* Invoice — only on standalone orders; grouped orders get one
          invoice button on the group header instead. */}
      {onInvoice && !hideOrderNumber && (
        <div style={{ marginTop: 12, textAlign: "right" }}>
          <button onClick={(e) => { e.stopPropagation(); onInvoice(); }}
                  style={{ background: "none", border: `1px solid ${T_NEW.ink12}`, cursor: "pointer",
                           padding: "6px 12px", borderRadius: T_NEW.r1, color: T_NEW.ink,
                           fontSize: T_NEW.micro, fontFamily: T_NEW.fontMono,
                           textTransform: "uppercase", letterSpacing: 0.6, fontWeight: 700 }}>
            {t("invoice.cta")}
          </button>
        </div>
      )}
    </div>
  );
}

// OrderGroup — collapsible wrapper for a multi-item cart checkout.
// Header shows the shared order number, item count, an aggregate stage
// pill, and the combined total; the body lists each line item as a
// normal OrderRow (so per-item status, payment, consolidation all keep
// working untouched).
function OrderGroup({ members, t, selected, onToggle, onChanged, onInvoice, onOpenDm }) {
  const [open, setOpen] = useState(true);
  const orderNumber = members.find((m) => m.orderNumber)?.orderNumber;
  const stage = groupStage(members);
  const stageTone =
    stage === "delivered" ? "bottle" :
    (stage === "cancelled" || stage === "unavailable") ? "danger" :
    "persimmon";
  // Combined total across line items (grand totals incl. fee when present).
  const totalUsd = members.reduce(
    (s, m) => s + ((m.grandTotalUsdCents ?? m.totalUsdCents) || 0), 0) / 100;
  const totalRub = members.reduce(
    (s, m) => s + ((m.grandTotalRubCents ?? m.totalRubCents) || 0), 0) / 100;

  return (
    <div style={{ border: `1.5px solid ${T_NEW.persimmon}40`, borderRadius: T_NEW.r2,
                  background: T_NEW.paper, overflow: "hidden" }}>
      {/* Header */}
      <div onClick={() => setOpen((x) => !x)}
           style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 16px",
                    cursor: "pointer", background: `${T_NEW.persimmon}0C` }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          {orderNumber && (
            <Mono size={T_NEW.micro} color={T_NEW.persimmon}
                  style={{ display: "block", fontWeight: 700 }}>
              № {orderNumber}
            </Mono>
          )}
          <Mono size={T_NEW.meta} color={T_NEW.ink56} style={{ display: "block", marginTop: 2 }}>
            {t("orders.group.items", { n: members.length })}
          </Mono>
        </div>
        <StatusPill tone={stageTone}>{t(`order.status.${stage}`)}</StatusPill>
        <div style={{ textAlign: "right", flexShrink: 0 }}>
          <Serif size={T_NEW.bodyLg} color={T_NEW.ink} style={{ display: "block", lineHeight: 1 }}>
            ${totalUsd.toFixed(2)}
          </Serif>
          <span style={{ fontSize: T_NEW.micro, color: T_NEW.ink56, fontFamily: T_NEW.fontMono,
                         marginTop: 3, display: "block" }}>
            ≈ ₽{totalRub.toFixed(0)}
          </span>
        </div>
        <span style={{ fontFamily: T_NEW.fontMono, color: T_NEW.ink56, fontSize: T_NEW.body,
                       width: 14, textAlign: "center" }}>
          {open ? "−" : "+"}
        </span>
      </div>

      {/* Line items */}
      {open && (
        <div style={{ display: "flex", flexDirection: "column", gap: 10, padding: "10px 12px 12px" }}>
          {members.map((o) => (
            <OrderRow key={o.id} o={o} t={t}
              selectable={o.status === "received"}
              selected={selected.has(o.id)}
              onToggle={() => onToggle(o.id)}
              onChanged={onChanged}
              onOpenDm={onOpenDm}
              hideOrderNumber />
          ))}
          {onInvoice && (
            <div style={{ textAlign: "right" }}>
              <button onClick={onInvoice}
                      style={{ background: "none", border: `1px solid ${T_NEW.ink12}`, cursor: "pointer",
                               padding: "6px 12px", borderRadius: T_NEW.r1, color: T_NEW.ink,
                               fontSize: T_NEW.micro, fontFamily: T_NEW.fontMono,
                               textTransform: "uppercase", letterSpacing: 0.6, fontWeight: 700 }}>
                {t("invoice.cta")}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// PaymentBlock — collapsed by default; expands on click into a panel
// showing each enabled payment method + an upload field. Submitting
// uploads the image, then POSTs payment-proof so the operator sees the
// order in their review queue.
function PaymentBlock({ order, onUploaded, onOpenDm }) {
  const t = useT();
  const lang = useLang();
  const toast = useToast();
  const [methods, setMethods] = useState(null);
  const [open, setOpen] = useState(false);
  const [chosen, setChosen] = useState("");
  const [uploading, setUploading] = useState(false);
  const [requesting, setRequesting] = useState(false);
  const fileRef = useRef(null);

  // Ask the seller for a QR/link in the personal DM, then jump into it.
  const requestLink = async () => {
    setRequesting(true);
    try {
      const r = await api.requestPaymentLink(order.id);
      toast.success(t("order.payment.requestSent"));
      if (r?.dmId && onOpenDm) onOpenDm(r.dmId);
    } catch (e) { toast.error(e.message); }
    finally { setRequesting(false); }
  };

  useEffect(() => {
    if (!open || methods) return;
    api.getPaymentMethods().then(setMethods).catch((e) => toast.error(e.message));
  }, [open, methods, toast]);

  const handleFile = async (file) => {
    if (!file) return;
    if (!chosen) { toast.error(t("order.payment.pickMethodFirst")); return; }
    setUploading(true);
    try {
      const up = await api.uploadImage(file);
      await api.uploadPaymentProof(order.id, up.path, chosen);
      toast.success(t("order.payment.uploaded.ok"));
      onUploaded?.();
    } catch (e) { toast.error(e.message); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  // Build the list of methods that are actually enabled (operator has
  // filled in the destination). If none are enabled we show a hint to
  // contact the operator via DM rather than a useless empty panel.
  //
  // USDT and USDC are multi-entry: each operator-configured network
  // surfaces as its own radio row. methodChosen sent to the server is
  // `usdt_<NETWORK>` / `usdc_<NETWORK>` (network already uppercased
  // server-side); we mirror that here so the round-trip is symmetric.
  //
  // The server's getPaymentMethods auto-promotes the legacy `usdt_trc20`
  // string into `usdt: [{network: "TRC20", address: ...}]` on read, so
  // we don't need a separate legacy row here — the array path catches it.
  const networkRows = (prefix, arr, baseLabel) =>
    Array.isArray(arr)
      ? arr.map((e) => ({
          key: `${prefix}_${e.network}`,
          label: `${baseLabel} · ${e.network}`,
          value: e.address,
          hint: "",
        }))
      : [];
  const usdtRows = networkRows("usdt", methods?.usdt, "USDT");
  const usdcRows = networkRows("usdc", methods?.usdc, "USDC");
  const enabled = methods ? [
    ...usdtRows,
    ...usdcRows,
    methods.sbp_phone  && { key: "sbp",        label: t("order.payment.sbp"),  value: methods.sbp_phone,
                            hint: methods.sbp_name ? `${methods.sbp_name}` : "" },
    methods.card_number && { key: "card",      label: t("order.payment.card"), value: methods.card_number,
                            hint: [methods.card_bank, methods.card_holder].filter(Boolean).join(" · ") },
  ].filter(Boolean) : [];

  return (
    <div style={{ marginTop: 12, padding: "12px 14px",
                  background: `${T_NEW.persimmon}0E`, borderRadius: T_NEW.r1,
                  border: `1px solid ${T_NEW.persimmon}40` }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }}
           onClick={() => setOpen((x) => !x)}>
        <span style={{ width: 22, height: 22, borderRadius: "50%", background: T_NEW.persimmon,
                       color: T_NEW.paper, display: "inline-flex", alignItems: "center", justifyContent: "center",
                       fontSize: 13, fontWeight: 700 }}>₽</span>
        <Mono color={T_NEW.ink} style={{ flex: 1, fontWeight: 700 }}>{t("order.payment.cta")}</Mono>
        <span style={{ fontSize: T_NEW.meta, color: T_NEW.ink56, fontFamily: T_NEW.fontMono }}>
          {open ? "—" : "+"}
        </span>
      </div>

      {open && (
        <div style={{ marginTop: 12, fontSize: T_NEW.meta, color: T_NEW.ink, fontFamily: T_NEW.fontSans }}>
          {/* Alternative: ask the seller for a QR/link in the personal chat. */}
          <button onClick={requestLink} disabled={requesting}
                  style={{ width: "100%", display: "flex", alignItems: "center", gap: 10,
                           padding: "10px 12px", marginBottom: 12, cursor: requesting ? "default" : "pointer",
                           background: T_NEW.paper, border: `1px solid ${T_NEW.persimmon}66`,
                           borderRadius: T_NEW.r1, textAlign: "left" }}>
            <span style={{ fontSize: 18 }}>💬</span>
            <span style={{ flex: 1 }}>
              <span style={{ display: "block", fontWeight: 700, color: T_NEW.ink, fontSize: T_NEW.bodySm }}>
                {t("order.payment.requestQr")}
              </span>
              <span style={{ display: "block", color: T_NEW.ink56, fontSize: T_NEW.micro, marginTop: 2 }}>
                {t("order.payment.requestQr.note")}
              </span>
            </span>
            <span style={{ color: T_NEW.persimmon, fontFamily: T_NEW.fontMono, fontSize: T_NEW.micro,
                           textTransform: "uppercase", letterSpacing: 0.6, fontWeight: 700 }}>
              {requesting ? "…" : "→"}
            </span>
          </button>
          {enabled.length > 0 && (
            <div style={{ fontSize: T_NEW.micro, color: T_NEW.ink56, textAlign: "center",
                          margin: "0 0 10px", textTransform: "uppercase", letterSpacing: 0.6,
                          fontFamily: T_NEW.fontMono }}>
              {t("order.payment.orPayDirect")}
            </div>
          )}
          {methods === null ? (
            <div style={{ color: T_NEW.ink56 }}>{t("common.loading")}</div>
          ) : enabled.length === 0 ? (
            <div style={{ color: T_NEW.ink56, fontStyle: "italic", fontFamily: T_NEW.fontSerif }}>
              {t("order.payment.noMethods")}
            </div>
          ) : (
            <>
              {/* Per-method radio rows. Click row to select; click value
                  to copy to clipboard for one-tap card-number transfers. */}
              {enabled.map((m) => (
                <label key={m.key}
                       style={{ display: "flex", alignItems: "flex-start", gap: 10,
                                padding: "8px 0", cursor: "pointer",
                                borderBottom: `1px dashed ${T_NEW.ink12}` }}>
                  <input type="radio" name={`pay-${order.id}`} value={m.key}
                         checked={chosen === m.key}
                         onChange={() => setChosen(m.key)}
                         style={{ marginTop: 4, accentColor: T_NEW.persimmon }} />
                  <div style={{ flex: 1 }}>
                    <Mono size={T_NEW.micro} color={T_NEW.ink56} style={{ display: "block" }}>
                      {m.label}
                    </Mono>
                    <div style={{ fontFamily: T_NEW.fontMono, fontSize: T_NEW.bodySm, marginTop: 4,
                                  color: T_NEW.persimmon, wordBreak: "break-all", cursor: "copy",
                                  fontWeight: 600 }}
                         onClick={(e) => {
                           e.preventDefault();
                           navigator.clipboard?.writeText(m.value).then(
                             () => toast.success(t("common.copied")),
                             () => {},
                           );
                         }}>
                      {m.value}
                    </div>
                    {m.hint && (
                      <div style={{ color: T_NEW.ink56, fontSize: T_NEW.micro, marginTop: 4,
                                    fontFamily: T_NEW.fontSans }}>
                        {m.hint}
                      </div>
                    )}
                  </div>
                </label>
              ))}

              {/* Optional operator instructions */}
              {(lang === "ru" ? methods.instructions_ru : methods.instructions_en) && (
                <div style={{ marginTop: 12, padding: "10px 12px", background: T_NEW.paper,
                              borderRadius: T_NEW.r1, color: T_NEW.ink56, fontSize: T_NEW.meta,
                              whiteSpace: "pre-wrap", fontFamily: T_NEW.fontSerif,
                              fontStyle: "italic", border: `1px solid ${T_NEW.ink12}` }}>
                  {lang === "ru" ? methods.instructions_ru : methods.instructions_en}
                </div>
              )}

              {/* The amount the customer needs to pay (highlighted so
                  there's no ambiguity which order this is for). */}
              <div style={{ marginTop: 14, padding: "10px 14px", background: T_NEW.paper,
                            borderRadius: T_NEW.r1, display: "flex", justifyContent: "space-between",
                            alignItems: "baseline", border: `1px solid ${T_NEW.ink12}` }}>
                <Mono size={T_NEW.micro} color={T_NEW.ink56}>{t("order.payment.amountDue")}</Mono>
                <Serif size={T_NEW.displayMd} color={T_NEW.persimmon}>
                  ${(order.totalUsdCents / 100).toFixed(2)}
                  <span style={{ fontSize: T_NEW.meta, color: T_NEW.ink56, fontFamily: T_NEW.fontMono, marginLeft: 8 }}>
                    · ₽{(order.totalRubCents / 100).toFixed(0)}
                  </span>
                </Serif>
              </div>

              <div style={{ marginTop: 12, display: "flex", gap: 10, alignItems: "center" }}>
                <Btn disabled={!chosen || uploading}
                     onClick={() => fileRef.current?.click()}>
                  {uploading ? t("common.loading") : t("order.payment.attachScreenshot")}
                </Btn>
                <span style={{ fontSize: T_NEW.micro, color: T_NEW.ink56, flex: 1, fontFamily: T_NEW.fontSans }}>
                  {t("order.payment.uploadHint")}
                </span>
                <input ref={fileRef} type="file" accept="image/*" style={{ display: "none" }}
                       onChange={(e) => handleFile(e.target.files?.[0])} />
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ResellerPriceEditor — inline view/edit of the reseller's own ruble
// bookkeeping price for a self-purchased order (migration 0075). Shows
// "Моя цена: ₽X (изм.)"; clicking expands a ruble input. Saves via
// PATCH /orders/:id/reseller-price.
function ResellerPriceEditor({ order, onChanged, t }) {
  const toast = useToast();
  const has = order.resellerPriceRubCents != null;
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(has ? String(Math.round(order.resellerPriceRubCents / 100)) : "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const cents = val.trim() === "" ? null : Math.round(parseFloat(val) * 100);
      if (cents != null && (!Number.isFinite(cents) || cents < 0)) {
        toast.error(t("order.rublePrice.invalid"));
        setSaving(false);
        return;
      }
      await api.setOrderResellerPrice(order.id, cents);
      toast.success(t("order.rublePrice.saved"));
      setEditing(false);
      onChanged?.();
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  if (!editing) {
    return (
      <div style={{ marginTop: 12, padding: "10px 12px", background: `${T_NEW.persimmon}0C`,
                    borderRadius: T_NEW.r1, border: `1px solid ${T_NEW.persimmon}33`,
                    display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <Mono size={T_NEW.micro} color={T_NEW.ink56} style={{ flex: 1 }}>
          {t("order.rublePrice.label")}:{" "}
          <span style={{ color: has ? T_NEW.ink : T_NEW.ink56, fontWeight: has ? 700 : 400 }}>
            {has ? `₽${(order.resellerPriceRubCents / 100).toFixed(0)}` : t("order.rublePrice.unset")}
          </span>
        </Mono>
        <button onClick={() => setEditing(true)}
                style={{ background: "none", border: "none", color: T_NEW.persimmon, cursor: "pointer",
                         padding: 0, fontSize: T_NEW.micro, fontFamily: T_NEW.fontMono,
                         textTransform: "uppercase", letterSpacing: 0.6, fontWeight: 700,
                         textDecoration: "underline" }}>
          {has ? t("common.edit") : t("order.rublePrice.set")}
        </button>
      </div>
    );
  }
  return (
    <div style={{ marginTop: 12, display: "flex", gap: 6, alignItems: "center" }}>
      <span style={{ fontSize: 15, color: T_NEW.ink56, fontFamily: T_NEW.fontMono }}>₽</span>
      <input type="number" min="0" value={val} onChange={(e) => setVal(e.target.value)}
             inputMode="numeric"
             style={{ width: 120, padding: "8px 10px", fontSize: T_NEW.bodySm, background: T_NEW.paper,
                      color: T_NEW.ink, border: `1px solid ${T_NEW.ink12}`, borderRadius: T_NEW.r1,
                      fontFamily: T_NEW.fontMono }} />
      <button onClick={save} disabled={saving}
              style={{ padding: "6px 14px", fontSize: T_NEW.micro, background: T_NEW.persimmon,
                       color: T_NEW.paper, border: "none", borderRadius: T_NEW.r1, cursor: "pointer",
                       fontFamily: T_NEW.fontMono, textTransform: "uppercase", letterSpacing: 0.7, fontWeight: 700 }}>
        {saving ? "…" : t("common.save")}
      </button>
      <button onClick={() => { setEditing(false); setVal(has ? String(Math.round(order.resellerPriceRubCents / 100)) : ""); }}
              style={{ padding: "6px 14px", fontSize: T_NEW.micro, background: "transparent", color: T_NEW.ink56,
                       border: `1px solid ${T_NEW.ink12}`, borderRadius: T_NEW.r1, cursor: "pointer",
                       fontFamily: T_NEW.fontMono, textTransform: "uppercase", letterSpacing: 0.7 }}>
        {t("common.cancel")}
      </button>
    </div>
  );
}

function ShipmentRow({ s, t, onChanged }) {
  const toast = useToast();
  // Map shipment status → StatusPill tone. Delivered = bottle green,
  // cancelled = danger red, in-flight = persimmon, building/ready = ochre.
  const statusTone =
    s.status === "delivered" ? "bottle" :
    s.status === "cancelled" ? "danger"  :
    s.status === "shipped"   ? "persimmon" :
                               "ochre";
  // Customs banner color depends on overCustomsThreshold flag.
  const declaredUsd = (s.totalDeclaredUsdCents ?? 0) / 100;
  const thresholdUsd = (s.customsThresholdUsdCents ?? 22000) / 100;
  const customsRatio = declaredUsd / Math.max(thresholdUsd, 1);
  return (
    <div style={{ background: T_NEW.paperSoft, borderRadius: T_NEW.r2, padding: 16, border: `1px solid ${T_NEW.ink12}` }}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 12, gap: 12 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <Serif size={T_NEW.bodyLg} color={T_NEW.ink} style={{ display: "block", lineHeight: 1.2 }}>
            {t("shipment.label")} <span style={{ fontFamily: T_NEW.fontMono, color: T_NEW.ink56, fontSize: T_NEW.bodySm }}>#{s.id.slice(0, 8)}</span>
          </Serif>
          <Mono size={T_NEW.micro} color={T_NEW.ink56} style={{ display: "block", marginTop: 6 }}>
            {new Date(s.createdAt * 1000).toLocaleDateString()} · {s.orders.length} {t("shipment.itemsCount")}
          </Mono>
        </div>
        {StatusPill ? (
          <StatusPill tone={statusTone}>{t(`shipment.status.${s.status}`)}</StatusPill>
        ) : (
          <span style={{ fontSize: T_NEW.micro, color: T_NEW.persimmon, fontWeight: 700,
                         textTransform: "uppercase", letterSpacing: 0.7, fontFamily: T_NEW.fontMono }}>
            {t(`shipment.status.${s.status}`)}
          </span>
        )}
      </div>

      {/* Customs declared-value summary. Red banner when over 200 EUR
          threshold; subtle row otherwise. Each line item gets its own
          editable declared value if the shipment is still building. */}
      <div style={{ marginBottom: 12, padding: "10px 14px",
                    background: s.overCustomsThreshold ? `${T_NEW.danger}14` : T_NEW.paper,
                    borderRadius: T_NEW.r1, fontSize: T_NEW.meta,
                    color: s.overCustomsThreshold ? T_NEW.danger : T_NEW.ink,
                    border: `1px solid ${s.overCustomsThreshold ? T_NEW.danger : T_NEW.ink12}33`,
                    fontFamily: T_NEW.fontSans }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
          <Mono size={T_NEW.micro} color={s.overCustomsThreshold ? T_NEW.danger : T_NEW.ink56}>
            {t("shipment.customs.declared")}
          </Mono>
          <span style={{ fontFamily: T_NEW.fontMono, fontWeight: 700 }}>
            ${declaredUsd.toFixed(2)} <span style={{ color: T_NEW.ink56, fontWeight: 400 }}>/ ${thresholdUsd.toFixed(0)}</span>
          </span>
        </div>
        {/* Mini progress bar for declared-value vs threshold */}
        <div style={{ marginTop: 8, height: 4, background: T_NEW.ink12, borderRadius: 2, overflow: "hidden" }}>
          <div style={{ width: `${Math.min(100, customsRatio * 100)}%`, height: "100%",
                        background: s.overCustomsThreshold ? T_NEW.danger : T_NEW.persimmon,
                        transition: "width 200ms" }} />
        </div>
        {s.overCustomsThreshold && (
          <div style={{ marginTop: 8, lineHeight: 1.5, fontStyle: "italic", fontFamily: T_NEW.fontSerif }}>
            {t("shipment.customs.overThreshold")}
          </div>
        )}
      </div>

      {/* Orders in this shipment. Editable declared-value field while
          the shipment is still 'building'. */}
      <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 12 }}>
        {s.orders.map((o) => (
          <div key={o.id} style={{ fontSize: T_NEW.meta, color: T_NEW.ink56,
                                    paddingLeft: 12, borderLeft: `2px solid ${T_NEW.persimmon}`,
                                    fontFamily: T_NEW.fontSans }}>
            <div style={{ color: T_NEW.ink }}>
              <span style={{ fontFamily: T_NEW.fontSerif, fontStyle: "italic" }}>
                {o.postTitle ?? t("orders.deletedPost")}
              </span>
              {o.variant ? ` · ${o.variant}` : ""}
              {" · "}
              <span style={{ fontFamily: T_NEW.fontMono, fontSize: T_NEW.micro, color: T_NEW.ink56 }}>
                {t("order.qty")}: {o.qty} · ${(o.totalUsdCents / 100).toFixed(2)}
              </span>
            </div>
            {s.status === "building" && (
              <DeclaredValueEditor order={o} onChanged={onChanged} t={t} />
            )}
            {s.status !== "building" && o.declaredValueUsdCents != null && (
              <div style={{ fontSize: T_NEW.micro, color: T_NEW.persimmon, marginTop: 4,
                            fontFamily: T_NEW.fontMono, textTransform: "uppercase", letterSpacing: 0.6 }}>
                {t("shipment.customs.declaredItem")}: ${(o.declaredValueUsdCents / 100).toFixed(2)}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Recipient */}
      {s.recipientName && (
        <div style={{ fontSize: T_NEW.micro, color: T_NEW.ink56, marginBottom: 12,
                      fontFamily: T_NEW.fontSans }}>
          → {s.recipientName}{s.shipAddress ? ` · ${s.shipAddress.split("\n").join(", ")}` : ""}
        </div>
      )}

      {/* Tracking (if filled in) */}
      {s.trackingNumber && (
        <div style={{ fontSize: T_NEW.meta, color: T_NEW.ink, background: T_NEW.paper,
                      padding: "10px 14px", borderRadius: T_NEW.r1, marginBottom: 12,
                      border: `1px solid ${T_NEW.ink12}`, display: "flex", alignItems: "baseline", gap: 8 }}>
          {s.carrier && (
            <Mono size={T_NEW.micro} color={T_NEW.ink56}>{s.carrier}</Mono>
          )}
          {s.trackingUrl
            ? <a href={s.trackingUrl} target="_blank" rel="noopener noreferrer"
                 style={{ color: T_NEW.persimmon, fontFamily: T_NEW.fontMono,
                          fontSize: T_NEW.bodySm, fontWeight: 600, textDecoration: "underline" }}>
                {s.trackingNumber}
              </a>
            : <span style={{ fontFamily: T_NEW.fontMono, fontSize: T_NEW.bodySm, fontWeight: 600 }}>
                {s.trackingNumber}
              </span>}
        </div>
      )}

      {/* Actions per status */}
      {s.status === "building" && (
        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <Btn variant="ghost" onClick={async () => {
            if (!await toast.confirm(t("shipment.cancel.confirm"))) return;
            try { await api.cancelShipment(s.id); onChanged(); toast.success(t("shipment.cancelled.ok")); }
            catch (e) { toast.error(e.message); }
          }}>{t("shipment.cancel")}</Btn>
          <Btn onClick={async () => {
            try { await api.markShipmentReady(s.id); onChanged(); toast.success(t("shipment.ready.ok")); }
            catch (e) { toast.error(e.message); }
          }}>{t("shipment.ready.cta")}</Btn>
        </div>
      )}
      {s.status === "ready" && (
        <div style={{ fontSize: T_NEW.meta, color: T_NEW.ink56, fontStyle: "italic",
                      fontFamily: T_NEW.fontSerif, textAlign: "center", padding: "8px 0" }}>
          {t("shipment.ready.waiting")}
        </div>
      )}
    </div>
  );
}

// Inline editor for a single order's declared customs value. Renders as
// a tiny "Declared: $X (edit)" affordance; clicking expands an input.
// Server validates that declared can't exceed 1.5× the purchase price.
function DeclaredValueEditor({ order, onChanged, t }) {
  const toast = useToast();
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(
    order.declaredValueUsdCents != null ? (order.declaredValueUsdCents / 100).toFixed(2) : "",
  );
  const [saving, setSaving] = useState(false);
  const displayUsd = order.effectiveDeclaredUsdCents / 100;
  const isOverride = order.declaredValueUsdCents != null;

  const save = async () => {
    setSaving(true);
    try {
      const cents = val.trim() === ""
        ? null
        : Math.round(parseFloat(val) * 100);
      if (cents != null && (!Number.isFinite(cents) || cents < 0)) {
        toast.error(t("shipment.customs.invalidValue"));
        setSaving(false);
        return;
      }
      await api.setOrderDeclaredValue(order.id, cents);
      toast.success(t("shipment.customs.saved"));
      setEditing(false);
      onChanged?.();
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  if (!editing) {
    return (
      <div style={{ fontSize: T_NEW.micro, marginTop: 4, display: "flex", gap: 8, alignItems: "center",
                    fontFamily: T_NEW.fontMono, textTransform: "uppercase", letterSpacing: 0.6 }}>
        <span style={{ color: isOverride ? T_NEW.persimmon : T_NEW.ink56 }}>
          {t("shipment.customs.declaredItem")}: ${displayUsd.toFixed(2)}
          {isOverride ? "" : ` (${t("shipment.customs.usingPrice")})`}
        </span>
        <button onClick={() => setEditing(true)}
                style={{ background: "none", border: "none", color: T_NEW.persimmon, cursor: "pointer",
                         padding: 0, fontSize: T_NEW.micro, fontFamily: T_NEW.fontMono,
                         textTransform: "uppercase", letterSpacing: 0.6, fontWeight: 700,
                         textDecoration: "underline" }}>
          {t("common.edit")}
        </button>
      </div>
    );
  }
  return (
    <div style={{ display: "flex", gap: 6, alignItems: "center", marginTop: 6 }}>
      <span style={{ fontSize: T_NEW.micro, color: T_NEW.ink56, fontFamily: T_NEW.fontMono }}>$</span>
      <input type="number" step="0.01" min="0" value={val} onChange={(e) => setVal(e.target.value)}
             placeholder={(order.totalUsdCents / 100).toFixed(2)}
             style={{ width: 92, padding: "6px 8px", fontSize: T_NEW.meta, background: T_NEW.paper,
                      color: T_NEW.ink, border: `1px solid ${T_NEW.ink12}`, borderRadius: T_NEW.r1,
                      fontFamily: T_NEW.fontMono }} />
      <button onClick={save} disabled={saving}
              style={{ padding: "4px 12px", fontSize: T_NEW.micro, background: T_NEW.persimmon,
                       color: T_NEW.paper, border: "none", borderRadius: T_NEW.r1, cursor: "pointer",
                       fontFamily: T_NEW.fontMono, textTransform: "uppercase", letterSpacing: 0.7, fontWeight: 700 }}>
        {saving ? "…" : t("common.save")}
      </button>
      <button onClick={() => { setEditing(false); setVal(order.declaredValueUsdCents != null ? (order.declaredValueUsdCents / 100).toFixed(2) : ""); }}
              style={{ padding: "4px 12px", fontSize: T_NEW.micro, background: "transparent", color: T_NEW.ink56,
                       border: `1px solid ${T_NEW.ink12}`, borderRadius: T_NEW.r1, cursor: "pointer",
                       fontFamily: T_NEW.fontMono, textTransform: "uppercase", letterSpacing: 0.7 }}>
        {t("common.cancel")}
      </button>
    </div>
  );
}
